// Central definitions for status enums.
//
// Each enum is declared once here as:
//   - a `*_STATUSES` tuple   → feeds `z.enum(...)` in forms and the DB schema's type
//   - a `*_STATUS_OPTIONS`    → feeds `EnumSelect` (value + human label)
//
// Keeping both in one place means a new status is added in exactly one spot and
// every form, filter, and create dialog picks it up.

export interface EnumOption {
  value: string;
  label: string;
}

// ── Widget (the reference entity — see src/db/schema/widget.ts) ──
//
// `widget` is the template's one example entity. When you add a real entity,
// declare its statuses here the same way and import them in its form/filter.
export const WIDGET_STATUSES = ["draft", "active", "archived"] as const;
export type WidgetStatus = (typeof WIDGET_STATUSES)[number];
export const WIDGET_STATUS_OPTIONS: readonly EnumOption[] = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
];

// ── Plan Audit ──
//
// The app's own domain: a Solution owns many Plan versions, each version owns
// many audit Runs, and a Run produces a score per Guide section plus a set of
// flags. See src/db/schema/plan-audit.ts.

export const ENGAGEMENT_STAGES = ["pre-sales", "post-signature"] as const;
export type EngagementStage = (typeof ENGAGEMENT_STAGES)[number];
export const ENGAGEMENT_STAGE_OPTIONS: readonly EnumOption[] = [
  { value: "pre-sales", label: "Pre-sales" },
  { value: "post-signature", label: "Post-signature" },
];

export const RUN_STATUSES = ["queued", "running", "complete", "failed"] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];
export const RUN_STATUS_OPTIONS: readonly EnumOption[] = [
  { value: "queued", label: "Queued" },
  { value: "running", label: "Running" },
  { value: "complete", label: "Complete" },
  { value: "failed", label: "Failed" },
];

// The six groups a scored section can sit under on the scorecard, in the order
// the Guide reads. Order matters: the scorecard renders groups in this sequence
// and splits them across two columns, so this tuple IS the page layout.
export const SCORE_GROUPS = ["Why", "Who", "What", "Where", "When", "Governance"] as const;
export type ScoreGroup = (typeof SCORE_GROUPS)[number];
// Guide sections can additionally be cross-cutting (a check applied to the whole
// plan, like the hallucination check) or the rubric itself, neither of which is a
// scorecard column. A section with no group yet is "Unassigned" — the Guide
// editor refuses to publish one, because it would have nowhere to score.
export const GUIDE_GROUPS = [...SCORE_GROUPS, "Cross-cut", "Rubric", "Unassigned"] as const;
export type GuideGroup = (typeof GUIDE_GROUPS)[number];
// The named blocks a Guide section is written in. The scorecard quotes these
// back verbatim under "What the Guide asks for", which is why they are a fixed
// set rather than free-form headings.
export const GUIDE_BLOCK_LABELS = [
  "WHAT IT IS",
  "WHY IT MATTERS",
  "EXAMPLE",
  "HOW TO DO IT WELL",
  "WATCH FOR",
  "HOW SCORING WORKS",
] as const;
export type GuideBlockLabel = (typeof GUIDE_BLOCK_LABELS)[number];
export const FLAG_KINDS = ["hallucination", "gap"] as const;
export type FlagKind = (typeof FLAG_KINDS)[number];
export const FLAG_KIND_OPTIONS: readonly EnumOption[] = [
  { value: "hallucination", label: "Hallucination" },
  { value: "gap", label: "Gap" },
];

// Why a flag was raised. Distinct from FLAG_KINDS: kind is what the flag IS,
// category is the failure pattern it belongs to — which is what Reporting counts
// to say where the Solution tool needs fixing.
export const FLAG_CATEGORIES = [
  "asserted-figure-no-source",
  "nymbl-content-in-client-voice",
  "intake-content-never-used",
  "plan-contradicts-itself",
] as const;
export type FlagCategory = (typeof FLAG_CATEGORIES)[number];
export const FLAG_CATEGORY_OPTIONS: readonly EnumOption[] = [
  { value: "asserted-figure-no-source", label: "Asserted figure, no source" },
  { value: "nymbl-content-in-client-voice", label: "Nymbl content in client voice" },
  { value: "intake-content-never-used", label: "Intake content never used" },
  { value: "plan-contradicts-itself", label: "Plan contradicts itself" },
];

// What the audit proposes doing to a weak section. Ordered least → most work,
// which is also how a reader should scan a scorecard.
export const PROPOSAL_TAGS = [
  "KEEP AS WRITTEN",
  "ADDITION",
  "REORDER",
  "RESTRUCTURED",
  "WRITE FROM SCRATCH",
] as const;
export type ProposalTag = (typeof PROPOSAL_TAGS)[number];
// Where a section's problem originated: the generating agent produced it, or the
// preamble/prompt framing did. Determines who fixes it.
export const PROBLEM_SOURCES = ["AGENT", "PREAMBLE"] as const;
export type ProblemSource = (typeof PROBLEM_SOURCES)[number];
// The three levers an audit's evidence can point at in the Solution tool. This
// is the feedback loop the plan exists to close.
export const RECOMMENDATION_TARGETS = ["prompt", "schema", "validator"] as const;
export type RecommendationTarget = (typeof RECOMMENDATION_TARGETS)[number];
export const RECOMMENDATION_TARGET_OPTIONS: readonly EnumOption[] = [
  { value: "prompt", label: "Prompt" },
  { value: "schema", label: "Schema" },
  { value: "validator", label: "Validator" },
];

// How a run classified each asset it found in the plan. "missing" is a section
// the Guide requires and the plan omits — scored 0 rather than dropped, so the
// absence stays visible instead of quietly shrinking the denominator.
export const ASSET_STATES = ["ok", "missing", "unrecognized"] as const;
export type AssetState = (typeof ASSET_STATES)[number];
// ── Audit log ──
//
// `action` is the `<entity>.<verb>` string stored on every audit_log row, and
// `entityType` is the coarse kind it groups under. Both are validated against
// these allowlists in `recordAudit` (src/lib/audit.ts) and drive the filter
// dropdowns in the admin viewer. Add a new action here when you audit a new
// mutation — the helper rejects anything not listed (see .claude/rules/audit.md).

export const AUDIT_ENTITY_TYPES = [
  "widget",
  "user",
  "auth",
  // Plan Audit. A single upload writes one row per entity it creates, rather
  // than one row for the whole submission: "when was this solution created, and
  // by whom" is a question the admin viewer answers by filtering on entity
  // type, and folding it into the run's row makes it unfindable.
  "solution",
  "plan_version",
  "audit_run",
] as const;
export type AuditEntityType = (typeof AUDIT_ENTITY_TYPES)[number];

export const AUDIT_ACTIONS = [
  // Reference-entity CRUD (src/examples/widget). Mirror this block per entity
  // you audit: `<entity>.created` / `.updated` / `.deleted`.
  "widget.created",
  "widget.updated",
  "widget.deleted",
  // Authentication events (captured via Better Auth hooks).
  "auth.login",
  "auth.login_failed",
  "auth.logout",
  "auth.signup",
  "auth.password_reset_requested",
  "auth.password_reset_completed",
  // Admin user management (Better Auth admin plugin endpoints).
  "user.created",
  "user.deleted",
  "user.role_changed",
  "user.banned",
  "user.unbanned",
  "user.password_reset",
  // Plan Audit — creating a solution, filing a plan version, queueing a run.
  // No `.updated`/`.deleted` yet: nothing in the app edits or removes these.
  "solution.created",
  "plan_version.created",
  "audit_run.created",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];
export const AUDIT_ACTION_OPTIONS: readonly EnumOption[] = AUDIT_ACTIONS.map((value) => ({
  value,
  label: value,
}));
